import clsx from 'clsx';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useInView } from 'react-intersection-observer';
import { MemoryRouter } from 'react-router-dom';

import { noopFunction } from '@vestico/utils';

import { WidgetContentEntry, userDataCompleted } from '@vestico/model';

import { ContentLayout, GlobalsContext, Spinner, SpinnerPositioning, SpinnerSize, UserContext, getResponseElementID } from '@vestico/shared';
import '@vestico/shared/src/layouts/globalWrapper.scss';
import resetStyle from '@vestico/shared/src/layouts/reset.module.css';

import { i18n } from '@vestico/i18n';

import { VisitsAPIContext, WidgetAuthContext } from '../../MainContext';
import { getStaticEntryElements } from '../../WidgetEntryContextStaticElements';
import { ConfigContext, ContentGlobalsContext } from '../../WidgetEventsManager';
import { CarouselMatchInfoModal } from '../carousel/modal/CarouselMatchInfoModal';
import '../fonts.css';
import { ContentDisplayCarouselLoader } from './ContentDisplayCarouselLoader';
import { ContentInteractionContext, ContentProductContext, ContentResponsesContext } from './ContentDisplayContext';

export interface ContentDisplayMainProps {
  entry: WidgetContentEntry;
}

export const ContentDisplayMain = ({ entry }: ContentDisplayMainProps) => {
  const visitsAPI = useContext(VisitsAPIContext);
  const { authInfo, resetAuth } = useContext(WidgetAuthContext);

  const { responses, selectedResponse, onSelectedResponse } = useContext(ContentResponsesContext);
  const { products } = useContext(ContentProductContext);
  const { setContentOpen, setContentBecameVisible, contentIsOpen } = useContext(ContentGlobalsContext);
  const { stylesConfig, useMobileLayout } = useContext(GlobalsContext);

  const { user } = useContext(UserContext);

  const { loading, hasMore, loadMore } = useContext(ContentInteractionContext);
  const { clientId } = useContext(ConfigContext);

  const [didInitiallyDisplayResponses, setDidInitiallyDisplayResponses] = useState(false);

  const [modal, setModal] = useState<ReactNode>(undefined);

  const { ref, inView, entry: viewEntry } = useInView();
  const [didBecomeVisible, setDidBecomeVisible] = useState(false);
  const { id: entryID, category, layoutType } = entry;

  useEffect(() => {
    // Theme inserted entries already render the insertion point
    // We need to make sure we hide/unhide accordingly and apply styles
    if (entry.isThemeInserted && responses && responses.length > 0) {
      const staticElements = getStaticEntryElements(entry);
      if (!staticElements) {
        console.warn(`Missing static elements but expected them for entry ${entry.id}`);
        return;
      }

      for (const staticElement of staticElements) {
        // Unhide the container element
        staticElement.style.display = 'block';
      }
    }
  }, [entry, responses]);

  useEffect(() => {
    if (inView && !didBecomeVisible) {
      setContentBecameVisible(entryID);
      setDidBecomeVisible(true);
    }
  }, [inView, entryID, didBecomeVisible, setContentBecameVisible]);

  useEffect(() => {
    if (didInitiallyDisplayResponses || !responses) {
      return;
    }
    setDidInitiallyDisplayResponses(true);
  }, [didInitiallyDisplayResponses, responses]);

  const [initialResponseIndex, setInitialResponseIndex] = useState<number | undefined>(undefined);

  const onClickResponse = useCallback(
    (index: number) => {
      if (!responses) {
        return;
      }

      setInitialResponseIndex(index);
      onSelectedResponse(responses[index]);
      setContentOpen(true, entryID);
      setModal(createPortal(<ContentDisplayCarouselLoader initialResponseIndex={index} entryID={entryID} />, document.body));
    },
    [responses, onSelectedResponse, setContentOpen, entryID]
  );

  useEffect(() => {
    if (
      selectedResponse &&
      contentIsOpen &&
      initialResponseIndex !== undefined &&
      responses &&
      responses[initialResponseIndex] !== selectedResponse
    ) {
      const id = requestAnimationFrame(() => {
        document.getElementById(getResponseElementID(selectedResponse))?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      return () => cancelAnimationFrame(id);
    }
    return noopFunction;
  }, [initialResponseIndex, responses, selectedResponse, contentIsOpen]);

  useEffect(() => {
    if (!contentIsOpen) {
      const timeout = setTimeout(() => setModal(undefined), 1000);
      return () => clearTimeout(timeout);
    }
    return noopFunction;
  }, [contentIsOpen]);

  const allowSpinner = useMemo(() => {
    const { layoutSubType } = entry;
    switch (layoutSubType) {
      case 'dynamic':
      case 'feed':
      case 'stories':
        return true;
      case 'singleRow':
      case 'singleRowFixed':
      case 'singleRowFreeScroll':
      case 'grid':
      case 'popup':
        return false;
    }
    throw new Error(`Unhandled layout ${layoutSubType}`);
  }, [entry]);

  const showUserMatchInfoCTA = useMemo(() => !user || !userDataCompleted(user, category, false, false), [category, user]);

  const [showUserMatchInfo, setShowUserMatchInfo] = useState(false);
  const onBeginUserMatchInfo = useCallback(() => {
    setShowUserMatchInfo(true);
  }, []);

  const onUserMatchInfoClose = useCallback(() => {
    setShowUserMatchInfo(false);
  }, []);

  const dir = i18n.dir();

  const recordedEntryID = useRef<string | undefined>(undefined);
  const [layoutDidAppear, setLayoutDidAppear] = useState(false);
  const onAppear = useCallback(() => setLayoutDidAppear(true), []);

  const widgetIsInView = useMemo(
    () => (inView && layoutType !== 'popup') || (layoutType === 'popup' && layoutDidAppear),
    [inView, layoutDidAppear, layoutType]
  );
  useEffect(() => {
    if (recordedEntryID.current || !visitsAPI || !responses || responses.length === 0 || !widgetIsInView) {
      return;
    }

    console.debug(`Recording impression: ${entryID}`);

    visitsAPI.recordImpression(entryID, user?.uid, { authInfo: authInfo, resetAuth });
    recordedEntryID.current = entryID;
  }, [authInfo, entryID, inView, resetAuth, responses, user?.uid, visitsAPI, widgetIsInView]);

  if (allowSpinner && !didInitiallyDisplayResponses && (products === undefined || responses === undefined)) {
    return <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.flex} />;
  }

  // Response came back but there's nothing to show
  if (responses === null) {
    return null;
  }

  const content = (
    <div className={clsx(resetStyle.root)} dir={dir} style={stylesConfig.getCustomizedStyles()} ref={ref}>
      <div>
        {responses ? (
          <ContentLayout
            responses={responses}
            onResponseClick={onClickResponse}
            entry={entry}
            showUserMatchInfoCTA={showUserMatchInfoCTA}
            onBeginUserMatchInfo={onBeginUserMatchInfo}
            loading={loading}
            loadMore={loadMore}
            hasMore={hasMore}
            clientID={clientId}
            useMobileLayout={useMobileLayout}
            onAppear={onAppear}
          />
        ) : null}
        {modal}
        <MemoryRouter>
          <CarouselMatchInfoModal
            category={category}
            showUserMatchInfo={showUserMatchInfo}
            onUserMatchInfoClose={onUserMatchInfoClose}
            refElement={viewEntry?.target}
          />
        </MemoryRouter>
      </div>
    </div>
  );

  return entry.layoutSubType === 'popup' ? createPortal(content, document.body) : content;
};
